/**
 * A recipe's verb step fills its params as text (the person's words, a table value), but a verb
 * takes typed params: validateOsVerbParams does no coercion, so "4" is never a slide number and
 * "true" is never a mute. Each filled param is typed once here by the verb's own schema, then
 * validated as the model's own call would be. Pure and browser-safe.
 */
import { getOsVerbSpec, validateOsVerbParams } from './os-verbs.js';

const has = (o: object, k: string) => Object.prototype.hasOwnProperty.call(o, k);

/** Type each filled param by the verb's schema, then validate: the params the verb runs with, or
 *  why not. An unknown param passes through for the validation to refuse by name. */
export function coerceVerbParams(
  verbId: string,
  filled: Readonly<Record<string, string | number | boolean>>,
): { ok: true; params: Record<string, unknown> } | { ok: false; error: string } {
  const spec = getOsVerbSpec(verbId);
  if (!spec) return { ok: false, error: `unknown verb ${verbId}` };
  const params: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(filled)) {
    const type = has(spec.params, k) ? spec.params[k]!.type : undefined;
    if (type === 'boolean') {
      if (v === true || v === 'true') params[k] = true;
      else if (v === false || v === 'false') params[k] = false;
      else return { ok: false, error: `${k} must be true or false` };
    } else if (type === 'number') {
      if (typeof v === 'number') params[k] = v;
      else if (typeof v === 'string' && /^-?\d+(\.\d+)?$/.test(v)) params[k] = Number(v);
      else return { ok: false, error: `${k} must be a number` };
    } else {
      params[k] = type === 'string' ? String(v) : v;
    }
  }
  const valid = validateOsVerbParams(spec, params);
  return valid.ok ? { ok: true, params } : { ok: false, error: valid.error };
}
